import { useState } from 'react';

import {
  CareerPathAiImpact,
  AppIcon,
  ChartCardModal,
  CareerPathAIDisruption,
} from '@backrs_ui/common-ui';

import { ProgressBarChart } from '@backrs_ui/common-ui/src/atoms/Charts/ProgressBarChart';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, SxProps, Theme, Collapse } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { useAgentContext } from './AiImpact.agentContext';
import {
  createAiImpactSectionData,
  createAiImpactModalData,
  calculateOverallAiImpact,
} from '../util';

const STRINGS = appStrings.modules.careerPath.detailPage.aiImpact;

type AiImpactProps = Readonly<{
  aiImpact: CareerPathAiImpact;
  careerPathAIDisruption: CareerPathAIDisruption;
}>;

export function AiImpact({ aiImpact, careerPathAIDisruption }: AiImpactProps) {
  useAgentContext(aiImpact, careerPathAIDisruption);

  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSectionToggle = (field: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(field)) {
        newSet.delete(field);
      } else {
        newSet.add(field);
      }

      return newSet;
    });
  };

  const isSectionOpen = (field: string) => openSections.has(field);

  const { score } = calculateOverallAiImpact(aiImpact);

  // Get structured AI impact data
  const aiImpactData = createAiImpactSectionData(aiImpact, careerPathAIDisruption);

  return (
    <Box sx={outerContainerSx}>
      <Box sx={contentContainerSx}>
        {/* overall impact of ai + chart */}
        <Box sx={aiDisruptionContainerSx}>
          <Box sx={aiDisruptionHeaderSx}>
            <Typography
              variant="text-sm-semibold"
              color={(theme: Theme) => theme.backrs.colors['Gray/900']}
            >
              {STRINGS.aiDisruption}
            </Typography>
            <Box
              id="career-path-ai-disruption-info-button"
              sx={{ cursor: 'pointer' }}
              onClick={() => setIsModalOpen(true)}
            >
              <AppIcon iconName="InfoCircle" color="Colors/Foreground/fg-quinary" diameter="16px" />
            </Box>
          </Box>

          <Box sx={aiDisruptionBodySx}>
            <Typography
              sx={{ paddingBottom: (theme: Theme) => theme.backrs.spacing['spacing-2xl'] }}
              variant="display-sm-medium"
            >
              {score}
            </Typography>
            <Box sx={aiDisruptionChartContainerSx}>
              {aiImpactData &&
                aiImpactData.map((item) => (
                  <Box key={item?.field} sx={aiDisruptionChartTextSx}>
                    <Typography variant="text-xs-medium" sx={aiDisruptionChartTextTitleSx}>
                      {item?.title}
                    </Typography>
                    <ProgressBarChart
                      max={5}
                      value={item?.level || 1}
                      tooltipLabel={item?.value}
                      color={item?.color}
                    />
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>

        {/* AI impact sections - dropdowns */}
        <Box sx={aiImpactSectionsSx}>
          {aiImpactData &&
            aiImpactData.map((item) => (
              <Box key={item?.field} sx={textDropdownSx(isSectionOpen(item?.field || ''))}>
                <Box
                  id={`career-path-ai-impact-dropdown-${item?.field?.toLowerCase().replace(/\s+/g, '-') || ''}`}
                  sx={textChevronSx}
                  onClick={() => handleSectionToggle(item?.field || '')}
                >
                  <Typography
                    variant="text-md-semibold"
                    color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-brand-tertiary']}
                    sx={{ cursor: 'pointer' }}
                  >
                    {item?.title}
                  </Typography>
                  <Box sx={chevronSx}>
                    <AppIcon
                      iconName={isSectionOpen(item?.field || '') ? 'ChevronDown' : 'ChevronRight'}
                      diameter="20px"
                      color="Colors/Text/text-brand-tertiary"
                    />
                  </Box>
                </Box>
                <Collapse in={isSectionOpen(item?.field || '')} timeout={200}>
                  <Typography
                    variant="text-sm-regular"
                    color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
                    sx={descriptionTextSx}
                  >
                    <ReactMarkdown>{item.description}</ReactMarkdown>
                  </Typography>
                </Collapse>
              </Box>
            ))}
        </Box>
      </Box>
      <ChartCardModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        icon="AnnotationInfo"
        modalWidth="sm"
        variant="ai-impact"
        modalData={createAiImpactModalData(aiImpact)}
      />
    </Box>
  );
}

const aiImpactSectionsSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const textDropdownSx =
  (isOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: isOpen ? backrs.spacing['spacing-none'] : backrs.spacing['spacing-md'],
  });

const textChevronSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-xs'],
});

const outerContainerSx: SxProps<Theme> = {
  overflow: 'hidden',
  maxWidth: '100%',
};

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-5xl'],
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  '@container (min-width: 600px)': {
    gridTemplateColumns: '1.3fr 1fr',
  },
});

const aiDisruptionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  maxWidth: '100%',
  overflow: 'hidden',
});

const aiDisruptionHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  paddingY: backrs.spacing['spacing-lg'],
  paddingX: backrs.spacing['spacing-2xl'],
});

const aiDisruptionBodySx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Gray/200']}`,
  borderRadius: backrs.radius['radius-xl'],
  padding: backrs.spacing['spacing-2xl'],
});

const aiDisruptionChartContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
});

const aiDisruptionChartTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  '@container (min-width: 350px)': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: backrs.spacing['spacing-2xl'],
  },
});

const chevronSx: SxProps<Theme> = {
  cursor: 'pointer',
  marginTop: '2px',
};

const descriptionTextSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: `-${backrs.spacing['spacing-md']}`,
});

const aiDisruptionChartTextTitleSx: SxProps<Theme> = {
  '@container (min-width: 350px)': {
    width: '150px',
    flexShrink: 0,
  },
};
